import { useState, useEffect, useMemo } from "react";
import {
  useNavigate,
  useLocation,
  matchPath,
  useParams,
} from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { getSchoolsDropdown } from "../../service/dashboardService";
import ChangePasswordModal from "../common/ChangePassword";
import SearchSelect from "../common/SearchSelect";
import "./navbar.css";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { schoolId, setSchoolId, schools, setSchools } = useApp();
  const { auth, logout } = useAuth();

  const [loading, setLoading] = useState(false);
  const [openPwdModal, setOpenPwdModal] = useState(false);

  const savedScId = Number(Cookies.get("sc_id") || 0);
  const admin = auth?.admin;

  const isSchoolDetailPage = matchPath(
    "/schools/detail/:sc_id",
    location.pathname,
  );

  useEffect(() => {
    if (isSchoolDetailPage && params.sc_id) {
      const urlSchoolId = Number(params.sc_id);
      if (urlSchoolId !== schoolId) {
        setSchoolId(urlSchoolId);
      }
    }
  }, [isSchoolDetailPage, params.id]);

  useEffect(() => {
    if (savedScId !== 0) return;

    const loadSchools = async () => {
      try {
        setLoading(true);
        const res = await getSchoolsDropdown();

        if (res.success) {
          setSchools(res.data);
          setSchoolId(res.data[0]?.sc_id || null);
        } else {
          toast.error(res.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  const handleSchoolChange = (value) => {
    setSchoolId(value);

    // Navigate ONLY if currently on detail page
    if (isSchoolDetailPage) {
      navigate(`/schools/detail/${value}`);
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);

  const schoolOptions = useMemo(
    () => schools.map((s) => ({ value: s.sc_id, label: s.name })),
    [schools],
  );

  // Up to two initials from the admin's name, as in the reference header
  const initials = useMemo(
    () =>
      (admin?.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("") || "A",
    [admin?.name],
  );

  return (
    <header className="flex-shrink-0 bg-white dark:bg-[#1C2431] border-b border-gray-200 dark:border-gray-700 h-16 flex items-center justify-between gap-4 px-6">
      {/* Left: school switcher (super admin only) */}
      <div className="flex items-center min-w-0">
        {savedScId === 0 && (
          <SearchSelect
            options={schoolOptions}
            value={schoolId}
            onChange={handleSchoolChange}
            loading={loading}
            icon="domain"
            placeholder="Select school"
            searchPlaceholder="Search schools"
            emptyText="No schools found"
            width={280}
          />
        )}
      </div>

      {/* Right: user menu */}
      <div className="flex items-center gap-4">
        <div className="user-area-wrap">
          <button
            type="button"
            className={`user-area${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="user-avatar">{initials}</span>
            <span className="user-name">{admin?.name || "Admin"}</span>
            <span className="material-symbols-outlined chevron-icon">
              keyboard_arrow_down
            </span>
          </button>

          {menuOpen && (
            <>
              <div className="user-menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="user-menu-panel" role="menu">
                <div className="user-menu-head">
                  <span className="user-menu-name">{admin?.name}</span>
                  {admin?.email && (
                    <span className="user-menu-email">{admin.email}</span>
                  )}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="user-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    setOpenPwdModal(true);
                  }}
                >
                  <span className="menu-item-icon menu-item-icon--pwd">
                    <span className="material-symbols-outlined">lock</span>
                  </span>
                  Change Password
                </button>
                <div className="user-menu-divider" />
                <button
                  type="button"
                  role="menuitem"
                  className="user-menu-item menu-item-logout"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                >
                  <span className="menu-item-icon menu-item-icon--logout">
                    <span className="material-symbols-outlined">logout</span>
                  </span>
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ChangePasswordModal
        open={openPwdModal}
        onClose={() => setOpenPwdModal(false)}
        onSubmit={() => {
          toast.success("Password updated successfully");
          setOpenPwdModal(false);
        }}
      />
    </header>
  );
};

export default Navbar;
